import Loadable from '@react-loadable/revised'
import React from 'react'
import { Navigate, Route, Routes } from 'react-router-dom'
import '@reach/tabs/styles.css'

import { LoadableLoading } from 'components/common/LoadableLoading'
import ConfigurationsIndex from 'components/configurations/ConfigurationsIndex'
import { useGetDisasterRecoveryTestPermissions } from 'components/disaster-recovery/hooks/use-get-disaster-recovery-test-permissions'
import { DISASTER_RECOVERY_TESTING_URLS } from 'components/disaster-recovery/utils/disaster-recovery-testing-routes'
import { getStatusChecksUrls } from 'components/status-checks/status-checks-urls-utils'
import { getTestSuitesUrls } from 'components/test-suites/test-suites-urls-utils'
import { TrackedGremlinRouteElement } from 'components/TrackedGremlinRouteElement'
import { SAML_CALLBACK_ROUTE_DEFINITION } from 'components/UnauthenticatedRoutes'
import { useWhatRanAccess } from 'components/what-ran/use-what-ran-access'
import { NO_TEAM } from 'context/AuthContext'
import useAuth from 'hooks/use-auth'
import { useFeatureFlags } from 'hooks/use-feature-flags'
import { prefetchPaginationData } from 'hooks/use-gremlin-query'
import useLongRunningApiData from 'hooks/use-long-running-api-data'
import { serviceQueryKeys } from 'hooks/use-services'
import { hasSupportAppPermission } from 'support/helpers/support-rbac'
import {
  attacksActivePagedGet,
  failureFlagsExperimentsActiveGet,
  reliabilityTestsActivePagedGet,
  scenariosActivePagedGet,
  schedulesAttacksPagedGet,
  schedulesScenariosPagedGet,
  schedulesServicesPagedGet
} from 'utils/endpoints-access'

const ApiDocs = Loadable({
  loader: () => import(/* webpackChunkName: "ApiDocs" */ 'components/ApiDocs'),
  loading: LoadableLoading
})

const JiraPage = Loadable({
  loader: () =>
    import(
      /* webpackChunkName: "JiraPage" */ 'components/rbac-settings/integrations/jira/JiraPage'
    ),
  loading: LoadableLoading
})

const OAuthPage = Loadable({
  loader: () =>
    import(
      /* webpackChunkName: "OAuthPage" */ 'components/rbac-settings/OAuthPage'
    ),
  loading: LoadableLoading
})

// New Create Experiment Routes
const AttacksIndex = Loadable({
  loader: () =>
    import(
      /* webpackChunkName: "AttacksIndex" */ 'components/attacks/AttacksIndex'
    ),
  loading: LoadableLoading
})

const ScenariosIndex = Loadable({
  loader: () =>
    import(
      /* webpackChunkName: "ScenariosIndex" */ 'components/scenarios/ScenariosIndex'
    ),
  loading: LoadableLoading
})

const SupportAppLegacy = Loadable({
  loader: () =>
    import(/* webpackChunkName: "SupportAppLegacy" */ 'support/index'),
  loading: LoadableLoading
})

const SupportApp = Loadable({
  loader: () =>
    import(
      /* webpackChunkName: "SupportApp" */ 'components/support/SupportIndex'
    ),
  loading: LoadableLoading
})

const ServicesIndex = Loadable({
  loader: () =>
    import(
      /* webpackChunkName: "ServicesIndex" */ 'components/services/ServicesIndex'
    ),
  loading: LoadableLoading
})

const GameDaysIndex = Loadable({
  loader: () =>
    import(
      /* webpackChunkName: "GameDaysIndex" */ 'components/gamedays/GameDaysIndex'
    ),
  loading: LoadableLoading
})

const SettingsIndex = Loadable({
  loader: () =>
    import(
      /* webpackChunkName: "SettingsIndex" */ 'components/rbac-settings/SettingsIndex'
    ),
  loading: LoadableLoading
})

const NotFoundPage = Loadable({
  loader: () =>
    import(/* webpackChunkName: "NotFoundPage" */ 'components/NotFoundPage'),
  loading: LoadableLoading
})

const FailureFlagsIndex = Loadable({
  loader: () =>
    import(
      /* webpackChunkName: "FailureFlagsIndex" */ 'components/failure-flags/FailureFlagsIndex'
    ),
  loading: LoadableLoading
})

const ReportsIndex = Loadable({
  loader: () =>
    import(
      /* webpackChunkName: "ReportsIndex" */ 'components/reliability-management/ReportsIndex'
    ),
  loading: LoadableLoading
})

const GettingStartedIndexOld = Loadable({
  loader: () =>
    import(
      /* webpackChunkName: "GettingStartedIndexOld" */ 'components/getting-started/GettingStartedIndexOld'
    ),
  loading: LoadableLoading
})

const NowRunningIndex = Loadable({
  loader: () =>
    import(
      /* webpackChunkName: "NowRunningIndex" */ 'components/now-running/NowRunningIndex'
    ),
  loading: LoadableLoading
})

const SchedulesIndex = Loadable({
  loader: () =>
    import(
      /* webpackChunkName: "SchedulesIndex" */ 'components/schedules/SchedulesIndex'
    ),
  loading: LoadableLoading
})

const WhatRanIndex = Loadable({
  loader: () =>
    import(
      /* webpackChunkName: "WhatRanIndex" */ 'components/what-ran/WhatRanIndex'
    ),
  loading: LoadableLoading
})

const Dashboard = Loadable({
  loader: () =>
    import(
      /* webpackChunkName: "Dashboard" */ 'components/dashboard/Dashboard'
    ),
  loading: LoadableLoading
})

const HelpPage = Loadable({
  loader: () =>
    import(/* webpackChunkName: "HelpPage" */ 'components/help/HelpPage'),
  loading: LoadableLoading
})

const NoTeamLandingPage = Loadable({
  loader: () =>
    import(
      /* webpackChunkName: "NoTeamLandingPage" */ 'components/NoTeamLandingPage'
    ),
  loading: LoadableLoading
})

const DisasterRecoveryTestingIndex = Loadable({
  loader: () =>
    import(
      /* webpackChunkName: "DisasterRecoveryTestingIndex" */ 'components/disaster-recovery/DisasterRecoveryTestingIndex'
    ),
  loading: LoadableLoading
})

const HomeIndex = Loadable({
  loader: () =>
    import(/* webpackChunkName: "HomeIndex" */ 'components/home/HomeIndex'),
  loading: LoadableLoading
})

export const AuthenticatedRoutes: React.FC = () => {
  const {
    largeScaleEvents: isLargeScaleEventsEnabled,
    qbrDashboards: isQbrDashboardsEnabled
  } = useFeatureFlags()
  const statusChecksUrls = getStatusChecksUrls()
  const testSuitesUrls = getTestSuitesUrls()

  const { canReadDisasterRecoveryTests, canReadDisasterRecoveryTestReports } =
    useGetDisasterRecoveryTestPermissions()

  const {
    currentTeamId,
    hasEndpointAccess,
    hasFailureFlags,
    hasFaultInjection,
    hasReliabilityManagement,
    session
  } = useAuth()

  const whatRanAccess = useWhatRanAccess()

  const userHasTeams = currentTeamId !== NO_TEAM

  const accessOptions = { teamId: currentTeamId }

  const canShowNowRunning =
    hasEndpointAccess(reliabilityTestsActivePagedGet, accessOptions) ||
    hasEndpointAccess(attacksActivePagedGet, accessOptions) ||
    hasEndpointAccess(scenariosActivePagedGet, accessOptions) ||
    hasEndpointAccess(failureFlagsExperimentsActiveGet, accessOptions)

  const canShowNewSchedules =
    hasEndpointAccess(schedulesAttacksPagedGet, accessOptions) ||
    hasEndpointAccess(schedulesScenariosPagedGet, accessOptions) ||
    hasEndpointAccess(schedulesServicesPagedGet, accessOptions)

  const canShowWhatRan = whatRanAccess.section

  const canShowLargeScale =
    isLargeScaleEventsEnabled &&
    canReadDisasterRecoveryTests &&
    canReadDisasterRecoveryTestReports

  useLongRunningApiData(userHasTeams)

  React.useEffect(() => {
    // Prefetch services because the api call is slow
    // Cannot use usePrefetchPaginationData due to feature flag condition
    if (userHasTeams && hasReliabilityManagement) {
      prefetchPaginationData(
        `/services/?pageSize=20&teamId=${currentTeamId}`,
        {},
        { queryKey: serviceQueryKeys.allPaged }
      )
    }
  }, [currentTeamId, hasReliabilityManagement, userHasTeams])

  return (
    <Routes>
      <Route element={<SettingsIndex />} path="/settings/*" />
      <Route
        element={SAML_CALLBACK_ROUTE_DEFINITION.element}
        path={`/users/${SAML_CALLBACK_ROUTE_DEFINITION.path}`}
      />
      {userHasTeams ? (
        <>
          <Route
            element={
              <TrackedGremlinRouteElement
                loggedIn
                component={<Navigate to="/dashboard" />}
                name="Onboarding"
              />
            }
            path="/onboarding"
          />
          <Route
            element={
              <TrackedGremlinRouteElement
                loggedIn
                component={<Dashboard />}
                name="Dashboard (FI)"
              />
            }
            path="/dashboard"
          />
          {canShowNowRunning && (
            <Route element={<NowRunningIndex />} path="/now-running/*" />
          )}
          {canShowNewSchedules && (
            <Route element={<SchedulesIndex />} path="/schedules/*" />
          )}
          {canShowWhatRan && (
            <Route element={<WhatRanIndex />} path="/run-history/*" />
          )}
          <Route
            element={
              <TrackedGremlinRouteElement
                loggedIn
                component={<ConfigurationsIndex />}
                name="Configurations"
              />
            }
            path="/configurations/*"
          />
          <Route
            element={<Navigate replace to={testSuitesUrls.base()} />}
            path={'/test-suites/*'}
          />
          <Route
            element={<Navigate replace to={statusChecksUrls.base()} />}
            path={'/health-checks/*'}
          />
          <Route
            element={
              <TrackedGremlinRouteElement
                loggedIn
                component={<JiraPage />}
                name="Jira"
              />
            }
            path="/jira"
          />
          <Route
            element={
              <TrackedGremlinRouteElement
                loggedIn
                component={<HelpPage />}
                name="Help"
              />
            }
            path="/help"
          />
          {/*
            This serves as a callback for the oauth integration flow
            After the user completes the flow with the OAuth provider and our
            API, the user will be redirected from the API back to the webapp
            at this path. The URL will contain a query parameter called `msg`
            which contains either a success or error message to display to the user
          */}
          <Route
            element={
              <TrackedGremlinRouteElement
                loggedIn
                component={<OAuthPage />}
                name="OAuth"
              />
            }
            path="/oauth-integration"
          />

          <Route
            element={
              hasSupportAppPermission(session) ? (
                <SupportAppLegacy />
              ) : (
                <Navigate to="/" />
              )
            }
            path="/support/*"
          />
          <Route
            element={
              hasSupportAppPermission(session) ? (
                <SupportApp />
              ) : (
                <Navigate to="/" />
              )
            }
            path="/supportnew/*"
          />

          {hasFaultInjection && (
            <>
              <Route element={<AttacksIndex />} path="/attacks/*" />
              <Route element={<ScenariosIndex />} path="/scenarios/*" />
              <Route element={<GameDaysIndex />} path="/gamedays/*" />
              <Route
                element={
                  <TrackedGremlinRouteElement
                    loggedIn
                    component={<ApiDocs />}
                    name="API Docs"
                  />
                }
                path="/api"
              />
            </>
          )}

          {hasFailureFlags && (
            <Route element={<FailureFlagsIndex />} path="/failure-flags/*" />
          )}

          {hasReliabilityManagement ? (
            <>
              <Route
                element={
                  isQbrDashboardsEnabled ? (
                    <HomeIndex />
                  ) : (
                    <GettingStartedIndexOld />
                  )
                }
                path="/*"
              />
              <Route element={<ReportsIndex />} path="/reports/*" />
              <Route element={<ServicesIndex />} path="/services/*" />
              {canShowLargeScale && (
                <Route
                  element={<DisasterRecoveryTestingIndex />}
                  path={`${DISASTER_RECOVERY_TESTING_URLS.base()}/*`}
                />
              )}
            </>
          ) : (
            <Route element={<Navigate to="/dashboard" />} path="/" />
          )}

          {/* these make it so the user doesn't get a 404 if they hit the back button after logging in */}
          <Route element={<Navigate to="/" />} path="/login/*" />
          <Route element={<Navigate to="/" />} path="/users/sso/*" />
          <Route element={<NotFoundPage />} path="*" />
        </>
      ) : (
        <>
          <Route element={<NoTeamLandingPage />} path="/noteam" />
          {/* everything else redirects to /noteam */}
          <Route element={<Navigate to="/noteam" />} path="*" />
        </>
      )}
    </Routes>
  )
}
